import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  Calculator,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { propostaService, Proposta } from "../../services/propostaService";
import { PropostaVisualizacao } from "../../components/PropostaTemplates";

const statusConfig = {
  rascunho: { label: "Rascunho", color: "bg-gray-500/20 text-gray-400", icon: Clock },
  enviada: { label: "Enviada", color: "bg-blue-500/20 text-blue-400", icon: Clock },
  aprovada: { label: "Aprovada", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  rejeitada: { label: "Rejeitada", color: "bg-red-500/20 text-red-400", icon: XCircle },
};

export function PropostaView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [proposta, setProposta] = useState<Proposta | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/area-cliente");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (id) {
      loadProposta();
    }
  }, [id]);

  const loadProposta = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await propostaService.getById(id);
      setProposta(data);
    } catch (error) {
      console.error("Erro ao carregar proposta:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumero = (numero: number, dataEmissao: string) => {
    const num = String(numero).padStart(3, "0");
    const data = new Date(dataEmissao);
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const ano = data.getFullYear();
    return `${num}-${mes}-${ano}`;
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!proposta) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="container mx-auto px-4 text-center">
          <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Proposta não encontrada</h2>
          <Link to="/portal/propostas" className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300">
            Voltar para propostas
          </Link>
        </div>
      </div>
    );
  }

  const status = statusConfig[proposta.status];
  const StatusIcon = status.icon;

  return (
    <>
      <Helmet>
        <title>Proposta #{formatNumero(proposta.numero, proposta.dataEmissao)} | Portal do Cliente</title>
      </Helmet>

      <div className="min-h-screen pt-20 pb-12 print:pt-0 print:pb-0 print:bg-white">
        <div className="container mx-auto px-4 print:px-8">
          {/* Header - Hidden on print */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 print:hidden"
          >
            <div className="flex items-center justify-between">
              <Link
                to="/portal/propostas"
                className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para propostas
              </Link>
            </div>
          </motion.div>

          {/* Status Badge e Ações */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 print:hidden"
          >
            <div className="flex items-center justify-between">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${status.color}`}>
                <StatusIcon className="w-5 h-5" />
                <span className="font-medium">{status.label}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir
                </button>
              </div>
            </div>
          </motion.div>

          {/* Proposta Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="bg-white rounded-xl shadow-xl p-8">
              <PropostaVisualizacao proposta={proposta} />
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
